import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './lib/auth'
import { ToastProvider } from './lib/toast'
import { DirtyProvider } from './lib/dirty'
import { Shell } from './components/layout/Shell'
import { Spinner } from './components/ui/Spinner'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Account from './pages/Account'
import ProductList from './pages/products/ProductList'
import ProductForm from './pages/products/ProductForm'
import CategoryTree from './pages/categories/CategoryTree'
import ColorList from './pages/colors/ColorList'
import VideoList from './pages/videos/VideoList'
import MediaLibrary from './pages/media/MediaLibrary'
import HomeContent from './pages/home/HomeContent'
import PostList from './pages/posts/PostList'
import PostForm from './pages/posts/PostForm'
import GiftList from './pages/gifts/GiftList'
import GiftForm from './pages/gifts/GiftForm'
import RedirectList from './pages/redirects/RedirectList'
import SettingsPage from './pages/SettingsPage'
import SeoHealth from './pages/SeoHealth'

/**
 * Cổng đăng nhập: chưa có phiên → về `/login`, nhớ đường dẫn để quay lại sau.
 * Đang kiểm tra phiên (lần load đầu) → spinner, không nhấp nháy về login rồi lại vào.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <div className="grid min-h-screen place-items-center"><Spinner /></div>
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  return <>{children}</>
}

/**
 * Chặn route mà API không cho vai hiện tại.
 *
 * Không có nó thì editor gõ tay `/settings` vẫn vào được trang, rồi trang đó nã một loạt
 * request 403 và render một màn hình đầy lỗi đỏ. Phân quyền thật vẫn ở API — đây chỉ là
 * để không dẫn người dùng vào ngõ cụt.
 *
 * Nằm **trong** `RequireAuth` nên không cần chờ `loading`: lúc còn `loading` thì
 * `RequireAuth` đã trả spinner, không có chuyện đá nhầm người đang đăng nhập hợp lệ.
 */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth()
  if (!isAdmin) return <Navigate to="/" replace />
  return <>{children}</>
}

/** Quản lý nội dung trở lên — sản phẩm, danh mục, màu sắc, video. */
function RequireManager({ children }: { children: React.ReactNode }) {
  const { isManager } = useAuth()
  if (!isManager) return <Navigate to="/" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <DirtyProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Shell>
                  <Routes>
                    <Route index element={<Dashboard />} />
                    <Route path="products" element={<RequireManager><ProductList /></RequireManager>} />
                    <Route path="products/new" element={<RequireManager><ProductForm /></RequireManager>} />
                    <Route path="products/:id" element={<RequireManager><ProductForm /></RequireManager>} />
                    <Route path="categories" element={<RequireManager><CategoryTree /></RequireManager>} />
                    <Route path="colors" element={<RequireManager><ColorList /></RequireManager>} />
                    <Route path="videos" element={<RequireManager><VideoList /></RequireManager>} />
                    <Route path="media" element={<MediaLibrary />} />
                    <Route path="trang-chu" element={<RequireAdmin><HomeContent /></RequireAdmin>} />
                    <Route path="posts" element={<PostList />} />
                    <Route path="posts/new" element={<PostForm />} />
                    <Route path="posts/:id" element={<PostForm />} />
                    {/* HTML thô → `requireAdmin` ở cả 5 endpoint API. Xem `apps/api/src/routes/gifts.ts`. */}
                    <Route path="qua-tang" element={<RequireAdmin><GiftList /></RequireAdmin>} />
                    <Route path="qua-tang/new" element={<RequireAdmin><GiftForm /></RequireAdmin>} />
                    <Route path="qua-tang/:id" element={<RequireAdmin><GiftForm /></RequireAdmin>} />
                    <Route path="redirects" element={<RequireAdmin><RedirectList /></RequireAdmin>} />
                    <Route path="seo" element={<RequireAdmin><SeoHealth /></RequireAdmin>} />
                    <Route path="settings" element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
                    <Route path="account" element={<Account />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Shell>
              </RequireAuth>
            }
          />
        </Routes>
        </DirtyProvider>
      </ToastProvider>
    </AuthProvider>
  )
}
